import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import { Footer } from "@/components/rentalmnp/shared/Footer";
import { Logo } from "@/components/rentalmnp/shared/Logo";

const TITLE = "Micro-BIC ou Réel LMNP : quel régime fiscal choisir ?";
const DESC =
  "Micro-BIC ou régime réel en LMNP : lequel est plus rentable pour vous ? Tableau comparatif + simulateur pour trancher en 2 min selon votre cas.";
const URL = "https://rentalmnp.com/comparateur-micro-bic-reel";

export const Route = createFileRoute("/comparateur-micro-bic-reel")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: "micro-BIC ou réel, comparateur LMNP, régime fiscal LMNP, abattement micro-BIC, amortissement LMNP" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESC,
          inLanguage: "fr-FR",
          author: { "@type": "Organization", name: "RentaLMNP" },
          publisher: { "@type": "Organization", name: "RentaLMNP", url: "https://rentalmnp.com/" },
          mainEntityOfPage: URL,
        }),
      },
    ],
  }),
  component: ComparateurPage,
});

function ComparateurPage() {
  return (
    <div className="min-h-screen bg-background">
      <main className="mx-auto max-w-4xl px-6 py-8 lg:px-12">
        <div className="mb-6 flex items-center justify-between gap-3">
          <Logo size={36} />
          <Link
            to="/"
            className="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-muted-foreground transition hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Retour à l'accueil</span><span className="sm:hidden">Accueil</span>
          </Link>
        </div>

        <header className="mt-6 mb-10">
          <h1 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Micro-BIC ou Réel LMNP : quel régime est le plus rentable ?
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            En LMNP, deux régimes fiscaux s'opposent. Le bon choix peut faire varier votre
            imposition de plusieurs milliers d'euros par an. Voici comment trancher.
          </p>
        </header>

        <section className="prose prose-slate max-w-none">
          <h2 className="text-2xl font-bold text-foreground">Le régime micro-BIC en bref</h2>
          <p className="text-muted-foreground">
            Le <strong>micro-BIC</strong> est le régime par défaut. Il applique un{" "}
            <strong>abattement forfaitaire de 50 %</strong> sur vos loyers (30 % depuis 2025
            pour les meublés de tourisme non classés). Vous êtes imposé sur la moitié de vos
            revenus locatifs, sans aucune comptabilité.
          </p>
          <ul className="mt-3 space-y-2 text-muted-foreground">
            <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" /> Simplicité : aucune comptabilité à tenir</li>
            <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" /> Idéal si vos charges réelles sont inférieures à 50 % des loyers</li>
            <li className="flex gap-2"><XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" /> Plafond de recettes : 77 700 €/an (barème 2026)</li>
            <li className="flex gap-2"><XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" /> Pas d'amortissement du bien possible</li>
          </ul>

          <h2 className="mt-10 text-2xl font-bold text-foreground">Le régime réel en bref</h2>
          <p className="text-muted-foreground">
            Le <strong>régime réel</strong> permet de déduire l'intégralité de vos charges
            (intérêts d'emprunt, taxe foncière, assurance, travaux, frais de gestion…) et
            surtout d'<strong>amortir le bien et le mobilier</strong>. Résultat fréquent :
            une imposition nulle ou quasi nulle pendant 10 à 15 ans.
          </p>
          <ul className="mt-3 space-y-2 text-muted-foreground">
            <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" /> Amortissement du bien (~85 % du prix sur 25-30 ans)</li>
            <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" /> Toutes les charges réelles déductibles</li>
            <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" /> Déficits reportables 10 ans</li>
            <li className="flex gap-2"><XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" /> Comptabilité obligatoire (~400-700 €/an)</li>
          </ul>

          <h2 className="mt-10 text-2xl font-bold text-foreground">Tableau comparatif</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary">
                  <th className="p-3 text-left font-semibold">Critère</th>
                  <th className="p-3 text-left font-semibold">Micro-BIC</th>
                  <th className="p-3 text-left font-semibold">Réel</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border"><td className="p-3 font-medium">Seuil de recettes</td><td className="p-3 text-muted-foreground">77 700 €/an</td><td className="p-3 text-muted-foreground">Aucun</td></tr>
                <tr className="border-b border-border"><td className="p-3 font-medium">Abattement / déduction</td><td className="p-3 text-muted-foreground">50 % forfaitaire</td><td className="p-3 text-muted-foreground">Charges réelles + amortissements</td></tr>
                <tr className="border-b border-border"><td className="p-3 font-medium">Comptabilité</td><td className="p-3 text-muted-foreground">Aucune</td><td className="p-3 text-muted-foreground">Liasse fiscale 2031</td></tr>
                <tr className="border-b border-border"><td className="p-3 font-medium">Travaux importants</td><td className="p-3 text-muted-foreground">Non déductibles</td><td className="p-3 text-muted-foreground">Déductibles</td></tr>
                <tr className="border-b border-border"><td className="p-3 font-medium">Crédit immobilier</td><td className="p-3 text-muted-foreground">Intérêts non déductibles</td><td className="p-3 text-muted-foreground">Intérêts déductibles</td></tr>
                <tr><td className="p-3 font-medium">Idéal pour</td><td className="p-3 text-muted-foreground">Bien payé cash, peu de charges</td><td className="p-3 text-muted-foreground">Bien à crédit ou avec travaux</td></tr>
              </tbody>
            </table>
          </div>

          <h2 className="mt-10 text-2xl font-bold text-foreground">Comment choisir en pratique ?</h2>
          <p className="text-muted-foreground">
            <strong>Règle simple :</strong> si vos charges réelles + amortissements dépassent
            50 % de vos loyers (presque toujours le cas avec un crédit), le régime réel est
            plus rentable. Sinon, micro-BIC pour la simplicité.
          </p>
          <p className="mt-3 text-muted-foreground">
            Mais le calcul exact dépend de votre TMI, du montant emprunté, des travaux, de
            la taxe foncière… <strong>Notre simulateur compare automatiquement les deux
            régimes</strong> sur votre cas réel et vous indique celui qui maximise votre
            cash-flow net.
          </p>

          <div className="mt-8 rounded-2xl border border-primary/20 bg-primary/5 p-6">
            <h3 className="text-lg font-bold text-foreground">Tester votre cas en 2 minutes</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Saisissez votre prix d'achat, vos loyers, vos charges et votre TMI. Le
              simulateur calcule votre rentabilité dans les deux régimes et recommande le
              plus avantageux.
            </p>
            <Link
              to="/"
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-md transition hover:-translate-y-0.5 hover:bg-primary-dark"
            >
              Lancer la simulation LMNP <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <h2 className="mt-10 text-2xl font-bold text-foreground">Pour aller plus loin</h2>
          <ul className="mt-3 space-y-2 text-muted-foreground">
            <li>→ <Link to="/methodologie" className="font-medium text-primary hover:underline">Comment nous calculons votre rentabilité</Link></li>
            <li>→ <Link to="/faq" className="font-medium text-primary hover:underline">FAQ LMNP : toutes les questions fréquentes</Link></li>
          </ul>
        </section>
      </main>
      <Footer />
    </div>
  );
}
